import React, { useState, useEffect, useRef } from "react";
import { GoogleMap, LoadScript, DirectionsRenderer } from "@react-google-maps/api";

const libraries = ["places"];
const mapContainerStyle = { width: "100%", height: "500px" };
const warehouseLocation = { lat: 23.934972, lng: 86.509889 };

export default function DeliveryCalculator() {
  const [currentPosition, setCurrentPosition] = useState(null);
  const [destination, setDestination] = useState("");
  const [directions, setDirections] = useState(null);
  const [output, setOutput] = useState("");
  const [alertMessage, setAlertMessage] = useState("");

  const [vehicle, setVehicle] = useState("");
  const [unloading, setUnloading] = useState("");
  const [product, setProduct] = useState("");
  const [brickType, setBrickType] = useState("Standard");
  const [brickQty, setBrickQty] = useState(0);

  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);
  const destinationInputRef = useRef(null);

  // Get user location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          setCurrentPosition({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          }),
        () => setAlertMessage("Location access denied. Enter destination manually.")
      );
    }
  }, []);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    if (window.google && destinationInputRef.current && !autocompleteRef.current) {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(destinationInputRef.current, {
        componentRestrictions: { country: "IN" },
        fields: ["formatted_address", "geometry"],
        types: ["address"],
      });

      autocompleteRef.current.addListener("place_changed", () => {
        const place = autocompleteRef.current.getPlace();
        if (place && place.formatted_address) setDestination(place.formatted_address);
      });
    }
  }, [destinationInputRef.current]);

  const handleCalculate = () => {
    setAlertMessage("");
    setOutput("");

    // ---------------- VALIDATIONS ----------------
    if (!destination || !vehicle || !unloading || !product || !brickQty) {
      setAlertMessage("Please fill all fields to calculate delivery.");
      return;
    }

    if (product !== "Flyash Bricks") {
      setAlertMessage("Contact company. Calculation is not allowed for selected product.");
      return;
    }

    if (brickQty % 1500 !== 0 && brickQty % 3500 !== 0) {
      setAlertMessage("Please fill quantity in multiples of 1500 or 3500.");
      return;
    }

    // Tractor restriction for 3500 category
    if (vehicle === "Tractor" && brickQty % 3500 === 0) {
      setAlertMessage("Tractor delivery is not allowed for 3500 quantity category. Use Tipper.");
      return;
    }

    const directionsService = new window.google.maps.DirectionsService();
    directionsService.route(
      { origin: warehouseLocation, destination, travelMode: "DRIVING" },
      (result, status) => {
        if (status === "OK") {
          const leg = result.routes[0].legs[0];
          const km = leg.distance.value / 1000;

          if (vehicle === "Tractor" && km > 300) {
            setAlertMessage("Distance too long. Please contact company for more info.");
            return;
          }

          // ---------------- CALCULATION LOGIC ----------------
          let vehicleCharge = 0;
          let unloadingCharge = 0;
          const loadCharge = brickQty * 0.2;
          const bricksPrice = brickQty * (brickType === "Standard" ? 5 : 6);

          // Tractor Charges
          if (vehicle === "Tractor") {
            const tractorCharges = [
              { max: 10, charge: 300 },
              { max: 15, charge: 400 },
              { max: 20, charge: 500 },
              { max: 25, charge: 600 },
              { max: 30, charge: 700 },
              { max: 35, charge: 800 },
              { max: 40, charge: 900 },
              { max: 45, charge: 1000 },
              { max: 50, charge: 1100 },
            ];
            const matchedCharge = tractorCharges.find((c) => km <= c.max);
            vehicleCharge = matchedCharge ? matchedCharge.charge : 1100 + (km - 50) * 30;

            if (unloading === "Labour") unloadingCharge = brickQty * (km <= 20 ? 0.2 : 0.3);
            else if (unloading === "Dumping") unloadingCharge = 0;
          }

          // Tipper Charges
          if (vehicle === "Tipper") {
            const tipperBaseCharges = [
              { max: 10, charge: 600 },
              { max: 15, charge: 800 },
              { max: 20, charge: 1000 },
              { max: 25, charge: 1200 },
              { max: 30, charge: 1400 },
            ];
            const matchedCharge = tipperBaseCharges.find((c) => km <= c.max);
            vehicleCharge = matchedCharge ? matchedCharge.charge : 1400 + Math.ceil((km - 30) / 5) * 200;

            if (unloading === "Labour") {
              const baseRate = 0.2;
              const extraRatePerKm = 0.005;
              const threshold = 20;
              unloadingCharge = brickQty * (baseRate + (km > threshold ? (km - threshold) * extraRatePerKm : 0));
            } else if (unloading === "Dumping Labour") unloadingCharge = 0;
          }

          const totalCharge = vehicleCharge + loadCharge + unloadingCharge + bricksPrice;

          // ---------------- OUTPUT ----------------
          setOutput(
            <div>
              <h3>Delivery Details</h3>
              <table style={{ borderCollapse: "collapse", width: "100%", maxWidth: 600 }}>
                <tbody>
                  <tr><td style={cellStyle}>Distance</td><td style={cellStyle}>{km.toFixed(2)} km</td></tr>
                  <tr><td style={cellStyle}>Vehicle</td><td style={cellStyle}>{vehicle}</td></tr>
                  <tr><td style={cellStyle}>Unloading</td><td style={cellStyle}>{unloading}</td></tr>
                  <tr><td style={cellStyle}>Brick Type</td><td style={cellStyle}>{brickType}</td></tr>
                  <tr><td style={cellStyle}>Quantity</td><td style={cellStyle}>{brickQty}</td></tr>
                  <tr><td style={cellStyle}>Vehicle Charge</td><td style={cellStyle}>₹{vehicleCharge.toFixed(0)}</td></tr>
                  <tr><td style={cellStyle}>Loading Charge</td><td style={cellStyle}>₹{loadCharge.toFixed(0)}</td></tr>
                  <tr><td style={cellStyle}>Unloading Charge</td><td style={cellStyle}>₹{unloadingCharge.toFixed(0)}</td></tr>
                  <tr><td style={cellStyle}>Bricks Price</td><td style={cellStyle}>₹{bricksPrice.toFixed(0)}</td></tr>
                  <tr style={{ fontWeight: "bold" }}><td style={cellStyle}>Total</td><td style={cellStyle}>₹{totalCharge.toFixed(0)}</td></tr>
                </tbody>
              </table>
              <p style={{ marginTop: 10, color: "red" }}>Note: IF TOLL WILL BE APPLICABLE WILL BE CHARGED EXTRA</p>
            </div>
          );

          setDirections(result);
        } else {
          setAlertMessage("Could not calculate route: " + status);
        }
      }
    );
  };

  return (
    <LoadScript googleMapsApiKey="AIzaSyAxS3QmWKGn-Pyk7rksXZwaEDyQLdAAANc" libraries={libraries}>
      <div style={containerStyle}>
        <h2 style={titleStyle}>Bricks Delivery Calculator</h2>

        {alertMessage && (
          <div style={{ backgroundColor: "#ffe5e5", color: "#ff0000", padding: 10, borderRadius: 6, marginBottom: 16 }}>
            {alertMessage}
          </div>
        )}

        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Origin:</label>
          <p style={{ margin: "4px 0 12px", color: "#777" }}>Hetbhajudi, Jharkhand 828109 (Fixed)</p>
          <input
            ref={destinationInputRef}
            id="destination-input"
            type="text"
            placeholder="Enter Destination"
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            style={inputStyle}
          />
        </div>

        <div style={gridStyle}>
          <div style={colStyle}>
            <label style={labelStyle}>Vehicle</label>
            <select value={vehicle} onChange={(e) => setVehicle(e.target.value)} style={selectStyle}>
              <option value="">Select</option>
              <option value="Tractor">Tractor</option>
              <option value="Tipper">Tipper</option>
            </select>
          </div>

          <div style={colStyle}>
            <label style={labelStyle}>Unloading</label>
            <select value={unloading} onChange={(e) => setUnloading(e.target.value)} style={selectStyle}>
              <option value="">Select</option>
              <option value="Labour">Labour</option>
              <option value="Dumping Labour">Dumping Labour</option>
            </select>
          </div>

          <div style={colStyle}>
            <label style={labelStyle}>Product</label>
            <select value={product} onChange={(e) => setProduct(e.target.value)} style={selectStyle}>
              <option value="">Select</option>
              <option value="Flyash Bricks">Flyash Bricks</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {product === "Flyash Bricks" && (
            <>
              <div style={colStyle}>
                <label style={labelStyle}>Brick Type</label>
                <select value={brickType} onChange={(e) => setBrickType(e.target.value)} style={selectStyle}>
                  <option value="Standard">Standard</option>
                  <option value="Premium">Premium</option>
                </select>
              </div>

              <div style={colStyle}>
                <label style={labelStyle}>Quantity</label>
                <input
                  type="number"
                  min={1}
                  value={brickQty}
                  onChange={(e) => setBrickQty(Number(e.target.value))}
                  style={inputStyle}
                />
              </div>
            </>
          )}
        </div>

        <button onClick={handleCalculate} style={buttonStyle}>
          Calculate Delivery
        </button>

        <div style={{ marginTop: 20 }}>{output}</div>

        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={currentPosition || warehouseLocation}
          zoom={12}
          onLoad={(map) => (mapRef.current = map)}
        >
          {directions && <DirectionsRenderer directions={directions} />}
        </GoogleMap>
      </div>
    </LoadScript>
  );
}

// Styles
const containerStyle = { maxWidth: 800, margin: "20px auto", padding: 24, background: "#fff", borderRadius: 12, boxShadow: "0 4px 16px rgba(0,0,0,0.1)" };
const titleStyle = { fontSize: 24, fontWeight: 600, marginBottom: 16, textAlign: "center", color: "#333" };
const labelStyle = { fontWeight: 500, color: "#555" };
const gridStyle = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 };
const colStyle = { display: "flex", flexDirection: "column" };
const selectStyle = { padding: 10, borderRadius: 8, border: "1px solid #ccc", fontSize: 14, outline: "none" };
const inputStyle = { padding: 10, borderRadius: 8, border: "1px solid #ccc", fontSize: 14, outline: "none", width: "100%" };
const buttonStyle = { width: "100%", padding: 12, background: "#007bff", color: "#fff", fontSize: 16, fontWeight: 500, border: "none", borderRadius: 8, cursor: "pointer", marginBottom: 20, transition: "background 0.3s" };
const cellStyle = { padding: 6, border: "1px solid #ccc" };
